import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx'
import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'
import { BaseCheckbox } from '../BaseCheckbox/BaseCheckbox'
import { useWeekdayLabel } from './lib'

const dayOptions = [
  { label: 'Понедельник', value: 1 },
  { label: 'Вторник', value: 2 },
  { label: 'Среда', value: 4 },
  { label: 'Четверг', value: 8 },
  { label: 'Пятница', value: 16 },
  { label: 'Суббота', value: 32 },
  { label: 'Воскресенье', value: 64 },
]

const ALL_DAYS_SUM = dayOptions.reduce((acc, day) => acc + day.value, 0) // 127

type WeekdaySelectProps = {
  value?: number
  onChange?: (sum: number) => void
  className?: string
  placeholder?: string
}

export const WeekdaySelect = ({
  value = ALL_DAYS_SUM,
  onChange,
  className,
  placeholder = 'Выбрать дни',
}: WeekdaySelectProps) => {
  const [selectedSum, setSelectedSum] = useState<number>(value)

  const isSelected = (dayValue: number) => (selectedSum & dayValue) === dayValue

  const toggleDay = (dayValue: number) => {
    let newSum = selectedSum
    if (isSelected(dayValue)) {
      newSum = selectedSum - dayValue
    } else {
      newSum = selectedSum + dayValue
    }
    setSelectedSum(newSum)
    onChange?.(newSum)
  }

  const selectedDays = dayOptions.filter((opt) => isSelected(opt.value))

  const label = useWeekdayLabel(selectedSum)

  return (
    <div className={clsx('flex flex-col gap-2 relative', className)}>
      <Listbox value={selectedSum} onChange={() => {}}>
        <ListboxButton
          className={clsx(
            'relative block w-full py-2 pl-2 text-left ring-1 ring-inset ring-current text-sm/6 rounded-lg px-3',
            'bg-white dark:bg-dark-bg text-text-secondary placeholder-text-secondary ring-card-border dark:ring-card-border-dark'
          )}
        >
          <div className="flex items-center justify-between">
            <span className="truncate">{selectedDays.length > 0 ? label : placeholder}</span>
            <ChevronDownIcon className="group pointer-events-none w-5 h-5" aria-hidden="true" />
          </div>
        </ListboxButton>

        <ListboxOptions
          anchor="bottom"
          transition
          className={clsx(
            'w-[var(--button-width)] rounded-lg ring-1 ring-inset p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            'bg-white dark:bg-dark-bg ring-card-border dark:ring-card-border-dark'
          )}
        >
          {dayOptions.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              as="div"
              className={clsx(
                'group flex cursor-pointer items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-light-purple dark:data-[focus]:bg-dark-purple'
              )}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                toggleDay(option.value)
              }}
            >
              <BaseCheckbox
                checked={isSelected(option.value)}
                onChange={() => toggleDay(option.value)}
                label={option.label}
                size="sm"
              />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}
